import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from './atoms/Input';
import Switch from './atoms/Switch';
import Button from './atoms/Button';
import { useCreateReferralMutation, useUpdateReferralMutation } from '../store';
import { ReferralDTO, CreateReferralDTO } from '../types/Referral';
import useGetParams from '../hooks/useGetParams';
import { generateDisplayId } from '../utils/generateCustomId';

const noSpaces = (value: string) => !/\s/.test(value);

interface Props {
  referral?: ReferralDTO;
  onSuccess: () => void;
  onCancel: () => void;
}

const validationSchema = yup.object<CreateReferralDTO>().shape({
  active: yup.boolean().required(),
  code: yup.string().required('Debes ingresar un código').test('no-spaces', 'No se permiten espacios', noSpaces),
  name: yup.string().required('Debes ingresar un nombre'),
  description: yup.string().required('Debes ingresar una descripcion'),
});

const CreateEditReferral: React.FC<Props> = ({ referral, onSuccess, onCancel }) => {
  const {
    register, handleSubmit, formState, setValue, watch,
  } = useForm<CreateReferralDTO>({
    defaultValues: {
      active: referral ? referral.active : true,
      code: referral?.code ?? generateDisplayId(),
      name: referral?.name,
      description: referral?.description,
    },
    resolver: yupResolver<CreateReferralDTO>(validationSchema),
  });

  const { eventId, businessId } = useGetParams();

  const [createReferral, resCreateReferral] = useCreateReferralMutation();
  const [updateReferral, resUpdateReferral] = useUpdateReferralMutation();

  useEffect(() => {
    if (resCreateReferral.isSuccess || resUpdateReferral.isSuccess) {
      onSuccess();
    }
  }, [resCreateReferral.isSuccess, resUpdateReferral.isSuccess]);

  const onSubmit = async (data: CreateReferralDTO) => {
    if (referral) {
      updateReferral({
        referral: { ...data, id: referral.id },
        eventId,
        businessId,
      });
    } else {
      createReferral({
        referral: data,
        eventId,
        businessId,
      });
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full flex flex-col gap-4">
        <div>
          <Switch
            label="Referido habilitado para la venta"
            labelClassName="text-text-primary"
            checked={watch('active')}
            onChange={(e) => setValue('active', e.target.checked)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Input
            {...register('code', { required: 'This field is required' })}
            id="code"
            label="Código"
            labelAbove={true}
            errMsg={formState.errors.code?.message}
          />

          <Input
            {...register('name', { required: 'This field is required' })}
            id="name"
            label="Nombre"
            labelAbove={true}
            errMsg={formState.errors.name?.message}
          />
        </div>

        <Input
          {...register('description', { required: 'This field is required' })}
          id="description"
          label="Descripción"
          labelAbove={true}
          errMsg={formState.errors.description?.message}
        />
        <div className="flex flex-row gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onCancel()}
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            variant="primary"
            loading={
                resCreateReferral?.isLoading || resUpdateReferral.isLoading
              }
            >
              Guardar
            </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateEditReferral;
